import { AfterViewChecked, ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Subject, takeUntil } from 'rxjs';
import { RktButtonDirective } from '@rocketfinancialcorp/rocket-ui/button';
import { RktFormComponent } from '@rocketfinancialcorp/rocket-ui/form';
import { RktIconComponent } from '@rocketfinancialcorp/rocket-ui/icon';
import { ActiveModal } from '@rocketfinancialcorp/rocket-ui/modal';

import { BusinessAccountAddress, FinancialAccountReviewData, FormModel, IntegratedFinancialAccountCreateModel } from '@shared/models';
import { fromAuth } from '@shared/store';
import { formAddressDeserializer } from '@shared/utils';

import {
  addressFields,
  createBAFinancialAccountFormFields,
  externalBankAccountFields,
  integratedBankAccountFields,
} from '../entity-form-fields';

@Component({
  selector: 'app-add-integrated-financial-account',
  templateUrl: './add-integrated-financial-account.component.html',
  imports: [RktButtonDirective, FormsModule, ReactiveFormsModule, RktFormComponent, RktIconComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: true,
})
export class AddIntegratedFinancialAccountComponent implements AfterViewChecked, OnDestroy {
  formModel: FormModel<IntegratedFinancialAccountCreateModel> = {
    category: 'INTEGRATED',
    subtype: 'CHECKING',
    cardOrBankAccount: 'bankAccount',
    name: undefined,
  };

  externalBankForm = new FormGroup({});

  financialAccountForm = new FormGroup({});

  integratedBankForm = new FormGroup({});

  integratedBankAccountFields = integratedBankAccountFields;

  externalBankAccountFields = externalBankAccountFields;

  createBAFinancialAccountFormFields = createBAFinancialAccountFormFields({
    categoryChangeHook: () => (this.addressFields = [...addressFields]),
  });

  addressForm = new FormGroup({});

  addressFields = addressFields;

  businessAccountId?: string;

  get formInvalid() {
    const { category } = this.formModel;
    return (
      !this.financialAccountForm.valid ||
      (category === 'EXTERNAL' && !this.externalBankForm.valid) ||
      (category === 'INTEGRATED' && !this.integratedBankForm.valid) ||
      (category === 'EXTERNAL' && !this.addressForm.valid)
    );
  }

  get isExternal(): boolean {
    return this.formModel.category === 'EXTERNAL';
  }

  get isIntegrated(): boolean {
    return this.formModel.category === 'INTEGRATED';
  }

  private destroy$ = new Subject<void>();

  constructor(
    public ref: ChangeDetectorRef,
    public activeModal: ActiveModal,
    private store: Store,
  ) {
    this.store
      .select(fromAuth.selectBusinessAccountDetails)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (businessAccount) => {
          const billingAddress = formAddressDeserializer(
            businessAccount?.addresses?.find(({ type, primary }) => type === 'PHYSICAL' && primary) || ({} as BusinessAccountAddress),
          );
          this.businessAccountId = businessAccount?.id;
          this.formModel = {
            ...this.formModel,
            ...billingAddress,
            legalName: businessAccount?.legalName,
          };
        },
      });
  }

  modalInitData(createData: FinancialAccountReviewData) {
    if (createData) {
      this.formModel = {
        ...this.formModel,
        name: createData.name,
      };
    }
  }

  public ngAfterViewChecked(): void {
    this.ref.detectChanges();
  }

  public ngOnDestroy(): void {
    this.financialAccountForm.markAsUntouched();
    this.addressForm.markAsUntouched();
    this.externalBankForm.markAsUntouched();
    this.integratedBankForm.markAsUntouched();

    this.destroy$.next();
    this.destroy$.complete();
  }

  onSubmit() {
    if (this.formInvalid) {
      return;
    }

    this.activeModal.close(this.formModel);
  }

  public onAccountNumberChange(): void {
    const { accountNumber, accountNumberConfirm } = this.formModel.bankAccount ?? {};
    const formEl = this.externalBankForm.get('bankAccount.accountNumberConfirm');
    if (!formEl) {
      return;
    }
    formEl.setErrors(null);
    if (accountNumber !== accountNumberConfirm) {
      formEl.setErrors({ matchError: { message: 'Oops, it looks like the account numbers do not match. Please re-enter.' } });
    }
  }
}
